import { useCallback, useSyncExternalStore } from "react";

export type LayoutMode = "list" | "grid";
/** Deals feed ("all"), its Top tab ("top"), Penny beta ("penny"), and the Leads page's All / Top views. */
export type LayoutSurface = "all" | "top" | "penny" | "leads" | "leadsTop" | "guestFeed";

export const LAYOUT_MODE_KEYS: Record<LayoutSurface, string> = {
  all: "dealhawk_products_layout_mode",
  top: "dealhawk_products_top_layout_mode",
  penny: "dealhawk_penny_beta_layout_mode",
  leads: "dealhawk_leads_layout_mode",
  leadsTop: "dealhawk_leads_top_layout_mode",
  guestFeed: "dealhawk_guest_feed_layout_mode",
};

// Same defaults as the app: feeds read best as rows; Top's "list" is its sideways rails of deal cards.
const DEFAULTS: Record<LayoutSurface, LayoutMode> = { all: "list", top: "list", penny: "list", leads: "list", leadsTop: "grid", guestFeed: "grid" };

const listeners = new Set<() => void>();
const memory: Partial<Record<LayoutSurface, LayoutMode>> = {};

export function getLayoutMode(surface: LayoutSurface): LayoutMode {
  const cached = memory[surface];
  if (cached) return cached;
  let stored: string | null = null;
  try { stored = localStorage.getItem(LAYOUT_MODE_KEYS[surface]); } catch {
    /* storage unavailable (private mode) */
  }
  const mode: LayoutMode = stored === "grid" || stored === "list" ? stored : DEFAULTS[surface];
  memory[surface] = mode;
  return mode;
}

export function setLayoutMode(surface: LayoutSurface, mode: LayoutMode) {
  if (getLayoutMode(surface) === mode) return;
  memory[surface] = mode;
  try { localStorage.setItem(LAYOUT_MODE_KEYS[surface], mode); } catch {
    /* storage unavailable: keep it for this session */
  }
  listeners.forEach((listener) => listener());
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/** The remembered list/grid choice for one surface, and a setter that saves it. */
export function useLayoutMode(surface: LayoutSurface): [LayoutMode, (mode: LayoutMode) => void] {
  const mode = useSyncExternalStore(subscribe, () => getLayoutMode(surface), () => DEFAULTS[surface]);
  const set = useCallback((next: LayoutMode) => setLayoutMode(surface, next), [surface]);
  return [mode, set];
}

/** Test-only: forget cached values so each case reads storage fresh. */
export function resetLayoutModesForTests() {
  for (const key of Object.keys(memory) as LayoutSurface[]) delete memory[key];
  listeners.clear();
}
